import { LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";
import { Button } from "./button";
import { useAuth } from "../../context/AuthContext";

/**
 * UserProfile component - displays Google authenticated user information
 * Shows user data from Google OAuth login with logout functionality
 */
export function UserProfile({ className = "", showEmail = true, showLogout = true }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Function to generate initials from user name
  const getInitials = (name) => {
    if (!name) return "GU";
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Use Google user data or fallback to defaults
  const userName = user?.name || user?.given_name || "Guest User";
  const userEmail = user?.email || "guest@example.com";
  const userPicture = user?.picture || null;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <Avatar className="w-10 h-10">
        {userPicture ? (
          <AvatarImage src={userPicture} alt={userName} />
        ) : null}
        <AvatarFallback className="bg-primary text-primary-foreground text-sm font-medium">
          {getInitials(userName)}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{userName}</p>
        {showEmail && (
          <p className="text-xs text-muted-foreground truncate">{userEmail}</p>
        )}
      </div>
      {showLogout && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleLogout}
          className="text-muted-foreground hover:text-destructive"
          title="Logout"
        >
          <LogOut className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}